import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  naarSearchParam,
  uitSearchParam,
  type Filterdescriptor,
  type Filterstand,
} from "@/components/ui/data-table-filter";
import { keepScrollPosition } from "@jbnl/shared-ui";

/**
 * The routes that carry `filter`-typed search params and so may bind a descriptor set to them -
 * see each route's own search schema in `router.tsx`.
 */
type FilterRoute =
  | "/wedstrijddagen"
  | "/reistijden/korpsen"
  | "/reistijden/ondersteuners"
  | "/ploegen"
  | "/ondersteuners";

/**
 * The chosen filters, in the route search params rather than in the table. Two things follow:
 * they survive picking another wedstrijddag or another tab, and a beheerder can send a colleague
 * a link to *the korpsen who ruled this dag out* - which is ADR-0028's argument for the chosen
 * record, one level in.
 */
export function useFilterStand<TData>(
  descriptors: Filterdescriptor<TData>[],
  from: FilterRoute,
) {
  const search: Record<string, string | undefined> = useSearch({ from });
  const navigate = useNavigate({ from });

  const stand = useMemo<Filterstand>(() => {
    const uit: Filterstand = {};

    for (const descriptor of descriptors) {
      const rauw = search[descriptor.id];

      // Absent is untouched, so the descriptor's own default stands. An empty string is a
      // filter somebody cleared on purpose, and must not be turned back on.
      if (rauw === undefined) {
        if (descriptor.standaard) uit[descriptor.id] = descriptor.standaard;
        continue;
      }

      uit[descriptor.id] = uitSearchParam(descriptor, rauw);
    }

    return uit;
  }, [descriptors, search]);

  const onStand = (nieuw: Filterstand) =>
    void navigate({
      ...keepScrollPosition,
      // Replace: narrowing a list is a change within the screen, not a stop to walk back through.
      replace: true,
      search: (vorige) => ({
        ...vorige,
        // Every filter is written once touched, so a cleared one is an empty value rather than
        // an absent key - see `naarSearchParam`.
        ...Object.fromEntries(
          descriptors.map((descriptor) => [
            descriptor.id,
            naarSearchParam(nieuw[descriptor.id]),
          ]),
        ),
        // Every route this hook binds to carries `pagina`: a narrower set can leave the old page
        // past the end of it, exactly as `onFuncties` on the Bevoegdheden route drops it by hand.
        pagina: undefined,
      }),
    });

  return { stand, onStand };
}
